import React, { useContext } from 'react';
import { Link } from 'react-router-dom';
import { ColumnProps } from 'antd/lib/table';

import EventActionMenu from '@totem/components/services/threats/EventActionMenu';
import EventsContext from '@totem/components/services/threats/eventsContainer/EventsContext';
import {
  sortNumberAscending,
  sortStringAscending,
} from '@totem/components/services/threats/eventsContainer_legacy/utilities';
import EventSeverityIndicator from '@totem/components/services/threats/EventSeverityIndicator';
import IBTable from '@totem/components/ui_components/antDesignOverrides/IBTable';
import InputContext from '@totem/components/ui_components/reportSettings/InputContext';
import { Event } from '@totem/types/events';
import { stringifyArray } from '@totem/utilities/tableFilterUtilities';

import '../../services/threats/events.css';

const EventsTable = () => {
  const { input, getFilterOptions, getFilterValues, handleTableChange } =
    useContext(InputContext);
  const { eventData, loading, totalRecords, onAction } =
    useContext(EventsContext);

  const onDoAction = (action: string, event: Event) => {
    if (typeof onAction === 'function') {
      onAction(action, event);
    }
  };

  const formatDateTIme = (dateIn: string) => {
    if (dateIn !== null) {
      if (!isNaN(Date.parse(dateIn))) {
        const dateVal = new Date(dateIn);
        return (
          <>
            {dateVal.toLocaleDateString()}
            <br />
            {dateVal.toLocaleTimeString()}
          </>
        );
      }
    }
    return <span>-</span>;
  };

  const getReferences = (event: Event) => {
    if (event.keys !== null) {
      return (
        <>
          {event.keys
            .filter(
              (key) =>
                key.system === 'Ticketing' &&
                key.instance === 'IBD' &&
                key.display !== null &&
                key.display !== '',
            )
            .map((key) => {
              return (
                <Link key={key.key} to={`/dashboard/ticket/${key.key}`}>
                  {key.display}
                </Link>
              );
            })}
        </>
      );
    }
    return <span />;
  };

  const columns: ColumnProps<Event>[] = [
    {
      title: 'Severity',
      dataIndex: 'severity',
      key: 'severity',
      className: 'severity-column',
      width: '95px',
      showSorterTooltip: true,
      sortDirections: ['ascend', 'descend'],
      render: (_, event: Event) => (
        <div
          onClick={() => onDoAction('showEventDetails', event)}
          style={{ cursor: 'pointer' }}
        >
          <EventSeverityIndicator severity={event.severity} />
        </div>
      ),
      sorter: (compA, compB) =>
        sortNumberAscending(compA.severity, compB.severity),
      filterMultiple: true,
      filteredValue: stringifyArray(getFilterValues('severity')),
      filters: getFilterOptions('severity'),
      filterDropdownProps: {
        placement: 'bottom',
      },
    },
    {
      title: 'Source',
      dataIndex: 'sourceType',
      key: 'sourceType',
      showSorterTooltip: true,
      sortDirections: ['ascend', 'descend'],
      sorter: (compA, compB) =>
        sortStringAscending(compA.sourceType, compB.sourceType),
      filterMultiple: true,
      filteredValue: stringifyArray(getFilterValues('sourceType')),
      filters: getFilterOptions('sourceType'),
    },
    {
      title: 'Type',
      dataIndex: 'eventType',
      key: 'eventType',
      showSorterTooltip: true,
      sortDirections: ['ascend', 'descend'],
      sorter: (compA, compB) =>
        sortStringAscending(compA.eventType, compB.eventType),
      filterMultiple: true,
      filteredValue: stringifyArray(getFilterValues('eventType')),
      filters: getFilterOptions('eventType'),
    },
    {
      title: 'Description',
      dataIndex: 'description',
      key: 'description',
    },
    {
      title: 'Event Time',
      dataIndex: 'eventTime',
      key: 'eventTime',
      render: (date) => formatDateTIme(date),
      sorter: (compA, compB) =>
        sortStringAscending(compA.eventTime, compB.eventTime),
      showSorterTooltip: true,
      sortDirections: ['ascend', 'descend'],
    },
    {
      title: 'Last Occur.',
      dataIndex: 'lastOccurrence',
      key: 'lastOccurrence',
      render: (date) => formatDateTIme(date),
      sorter: (compA, compB) =>
        sortStringAscending(compA.lastOccurrence, compB.lastOccurrence),
      showSorterTooltip: true,
      sortDirections: ['ascend', 'descend'],
    },
    {
      title: 'Ref',
      dataIndex: 'ref',
      key: 'ref',
      render: (_, event: Event) => <>{getReferences(event)}</>,
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      showSorterTooltip: true,
      sortDirections: ['ascend', 'descend'],
      sorter: (compA, compB) => sortStringAscending(compA.status, compB.status),
      render: (_, event: Event) => (
        <span>
          {event.status.charAt(0).toUpperCase() +
            event.status.toLowerCase().slice(1)}
        </span>
      ),
      filterMultiple: true,
      filteredValue: stringifyArray(getFilterValues('status')),
      filters: getFilterOptions('status'),
    },
    {
      title: 'Actions',
      width: 30,
      dataIndex: 'additionalOptions',
      key: 'additionalOptions',
      render: (_, event: Event) => (
        <div className="center-table-cell">
          <EventActionMenu
            event={event}
            onView={() => onDoAction('showEventDetails', event)}
            onAcknowledge={() => onDoAction('acknowledgeEvent', event)}
            onClear={() => onDoAction('clearEvent', event)}
            onCreateTicket={() => onDoAction('createTicket', event)}
            onShowAssociateTicket={() =>
              onDoAction('showAssociateTicketDialog', event)
            }
          />
        </div>
      ),
    },
  ];

  const getEvents = () => {
    if (eventData !== null) {
      if (eventData.events !== null) {
        return eventData.events;
      }
    }
    return [];
  };

  return (
    <IBTable
      showSorterTooltip
      columns={columns}
      dataSource={getEvents()}
      loading={loading}
      onChange={handleTableChange}
      pagination={{
        current: input.pagination.page,
        pageSize: input.pagination.pageSize,
        total: totalRecords,
        showSizeChanger: true,
      }}
      rowKey="id"
    />
  );
};

export default EventsTable;
